import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

// Active benefit that provides a discount
interface ActiveBenefit {
  id: string;
  benefit_type: string;
  benefit_name: string;
  description: string | null;
  discount_percent: number;
  expires_at: string;
}

interface Profile {
  id: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  email: string | null;
  is_club_member: boolean;
  created_at: string;
  birthday: string | null;
  marketing_consent: boolean;
  // Address fields for saved delivery address
  address_city: string | null;
  address_street: string | null;
  address_building_number: string | null;
  address_apartment: string | null;
  address_floor: string | null;
  address_entry_code: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  activeBenefits: ActiveBenefit[];
  loading: boolean;
  // UNIFIED: User is a club member if they are logged in AND is_club_member = true
  isMember: boolean;
  signInWithEmail: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUpWithEmail: (email: string, password: string, metadata?: { first_name?: string; last_name?: string; phone?: string }) => Promise<{ error: Error | null }>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  getDiscountedPrice: (originalPrice: number) => number;
  getBestDiscount: () => ActiveBenefit | null;
}

const AuthContext = createContext<AuthContextType | null>(null);

// Helper to get discount percent from benefit type
function getDiscountFromBenefitType(benefitType: string): number {
  switch (benefitType) {
    case 'birthday_discount': return 30;
    case 'half_year_discount': return 15;
    case 'welcome_gift': return 10;
    case 'annual_discount': return 5;
    default: return 0;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [activeBenefits, setActiveBenefits] = useState<ActiveBenefit[]>([]);
  const [loading, setLoading] = useState(true);

  // UNIFIED: User is a club member if they have an active session AND is_club_member = true
  const isMember = Boolean(user && profile?.is_club_member);

  // Fetch profile data
  const fetchProfile = async (userId: string) => {
    try {
      const { data: profileData } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();
      
      if (profileData) {
        setProfile(profileData as Profile);
        
        // Fetch active benefits if user is a club member
        if (profileData.is_club_member) {
          await fetchBenefitsForUser(userId, profileData.phone, profileData.email);
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  // Fetch benefits for user (via memberships or members table lookup)
  const fetchBenefitsForUser = async (userId: string, phone: string | null, email: string | null) => {
    try {
      // First try via memberships table (user_id linked)
      const { data: membershipData } = await supabase
        .from('memberships')
        .select('*')
        .eq('user_id', userId)
        .eq('active', true)
        .maybeSingle();
      
      let memberId: string | null = null;
      
      // If no membership, try looking up by phone/email in members table
      if (!membershipData) {
        if (phone) {
          const { data: memberData } = await supabase
            .from('members')
            .select('id')
            .eq('phone', phone)
            .maybeSingle();
          if (memberData) memberId = memberData.id;
        }
        if (!memberId && email) {
          const { data: memberData } = await supabase
            .from('members')
            .select('id')
            .eq('email', email)
            .maybeSingle();
          if (memberData) memberId = memberData.id;
        }
      }

      // Fetch benefits if we have a member ID
      if (memberId) {
        const now = new Date().toISOString();
        const { data: benefitsData } = await supabase
          .from('member_benefits')
          .select('*')
          .eq('member_id', memberId)
          .eq('is_redeemed', false)
          .lte('available_from', now)
          .gte('expires_at', now)
          .order('expires_at', { ascending: true });
        
        if (benefitsData) {
          const benefitsWithDiscount: ActiveBenefit[] = benefitsData.map(b => ({
            id: b.id,
            benefit_type: b.benefit_type,
            benefit_name: b.benefit_name,
            description: b.description,
            discount_percent: getDiscountFromBenefitType(b.benefit_type),
            expires_at: b.expires_at,
          }));
          setActiveBenefits(benefitsWithDiscount);
        }
      }
    } catch (error) {
      console.error('Error fetching benefits:', error);
    }
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          // Defer Supabase calls with setTimeout to prevent deadlock
          setTimeout(() => {
            fetchProfile(session.user.id);
          }, 0);
        } else {
          // Clear all data on logout
          setProfile(null);
          setActiveBenefits([]);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        fetchProfile(session.user.id);
      }
      
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signInWithEmail = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error: error as Error | null };
  };

  const signUpWithEmail = async (
    email: string, 
    password: string, 
    metadata?: { first_name?: string; last_name?: string; phone?: string }
  ) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/`,
        data: metadata, // phone is now included in metadata
      },
    });
    return { error: error as Error | null };
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setProfile(null);
    setActiveBenefits([]);
  };

  const refreshProfile = async () => {
    if (user) {
      await fetchProfile(user.id);
    }
  };

  // Get the best available discount from active benefits
  const getBestDiscount = (): ActiveBenefit | null => {
    if (activeBenefits.length === 0) return null;
    return activeBenefits.reduce((best, current) => 
      current.discount_percent > best.discount_percent ? current : best
    );
  };

  const getDiscountedPrice = (originalPrice: number): number => {
    const bestDiscount = getBestDiscount();
    if (!bestDiscount || bestDiscount.discount_percent <= 0) {
      return originalPrice;
    }
    const discount = (originalPrice * bestDiscount.discount_percent) / 100;
    return Math.round((originalPrice - discount) * 100) / 100;
  };

  return (
    <AuthContext.Provider value={{
      user,
      session,
      profile,
      activeBenefits,
      loading,
      isMember,
      signInWithEmail,
      signUpWithEmail,
      resetPassword,
      signOut,
      refreshProfile,
      getDiscountedPrice,
      getBestDiscount,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}
